/**
 * [Phase 2c.6-A.2.6] FullscreenToggle - reusable F11-equivalent button for
 * the standalone draft boards (snake + auction). Targets the entire board
 * surface (the document element by default) so the in-person draft on a
 * projector / second monitor goes truly edge-to-edge.
 *
 * Browser Fullscreen API:
 *   - `Element.requestFullscreen()` / `document.exitFullscreen()`
 *   - Vendor prefixes intentionally omitted - modern Chrome/Firefox/Safari
 *     all support the unprefixed methods. Old Safari (<16) needs
 *     `webkitRequestFullscreen` but that ship has sailed.
 *   - `fullscreenchange` event keeps the icon in sync if the user presses
 *     ESC or toggles via DevTools.
 *
 * Props:
 *   - targetRef? - ref to the element to fullscreen. Defaults to
 *     `document.documentElement` (whole page).
 *   - className? - pass-through styling (matches the theme/Wifi toggles
 *     already in the top bar).
 *   - color  - icon color (matches `fg` from the parent).
 */
import { useCallback, useEffect, useState } from "react";
import { Maximize2, Minimize2 } from "lucide-react";

export default function FullscreenToggle({
  targetRef,
  className = "w-7 h-7 rounded flex items-center justify-center",
  color = "currentColor",
  testId = "fullscreen-toggle",
}) {
  const [isFs, setIsFs] = useState(
    typeof document !== "undefined" && Boolean(document.fullscreenElement),
  );

  const toggle = useCallback(async () => {
    const target =
      (targetRef && targetRef.current) ||
      (typeof document !== "undefined" && document.documentElement);
    if (!target) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else if (target.requestFullscreen) {
        await target.requestFullscreen();
      }
    } catch (e) {
      // User gesture / iframe / sandboxed-frame errors land here. Surface
      // softly - no toast spam - and let the user retry.
      // eslint-disable-next-line no-console
      console.warn("[FullscreenToggle] toggle failed:", e?.message || e);
    }
  }, [targetRef]);

  useEffect(() => {
    const onChange = () => setIsFs(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  return (
    <button
      type="button"
      onClick={toggle}
      className={className}
      data-testid={testId}
      aria-label={isFs ? "Exit fullscreen" : "Enter fullscreen"}
      title={isFs ? "Exit fullscreen (ESC)" : "Fullscreen"}
    >
      {isFs ? (
        <Minimize2 size={14} color={color} />
      ) : (
        <Maximize2 size={14} color={color} />
      )}
    </button>
  );
}
